import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useIsMobile } from '../../hooks/useIsMobile';
import { listInstallers } from '../installers/api';
import { createTask, listTemplates } from './api';
export const TaskCreate = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [templates, setTemplates] = useState([]);
    const [installers, setInstallers] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [loadError, setLoadError] = useState(null);
    // Form fields
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [name, setName] = useState('');
    const [instructions, setInstructions] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [assignedInstallerId, setAssignedInstallerId] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    useEffect(() => {
        ;
        (async () => {
            setLoadingData(true);
            setLoadError(null);
            try {
                const [tmplResult, instResult] = await Promise.all([
                    listTemplates(),
                    listInstallers({ status: 'active', limit: 100 }),
                ]);
                setTemplates(tmplResult);
                setInstallers(instResult.items);
            }
            catch (e) {
                setLoadError(e.message ?? 'Failed to load form data');
            }
            finally {
                setLoadingData(false);
            }
        })();
    }, []);
    const handleTemplateChange = (templateId) => {
        setSelectedTemplateId(templateId);
        if (templateId) {
            const tmpl = templates.find((t) => t.id === templateId);
            if (tmpl) {
                setName(tmpl.name);
                setInstructions(tmpl.instructions);
            }
        }
        else {
            setName('');
            setInstructions('');
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim()) {
            setSubmitError('Task name is required.');
            return;
        }
        setSubmitting(true);
        setSubmitError(null);
        try {
            const task = await createTask({
                name: name.trim(),
                instructions: instructions.trim() || undefined,
                dueDate: dueDate || undefined,
                assignedInstallerId: assignedInstallerId || undefined,
                templateId: selectedTemplateId || undefined,
            });
            navigate(`/tasks/${task.id}`);
        }
        catch (e) {
            setSubmitError(e.message ?? 'Failed to create task');
            setSubmitting(false);
        }
    };
    const containerStyle = {
        maxWidth: isMobile ? '100%' : '640px',
        margin: '0 auto',
    };
    return (_jsx(Layout, { children: _jsxs("div", { style: containerStyle, children: [_jsxs("div", { style: styles.pageHeader, children: [_jsx("button", { style: styles.backButton, onClick: () => navigate('/tasks'), children: "\u2190 Back" }), _jsx("h2", { style: styles.heading, children: "New Task" })] }), loadError && _jsx("div", { style: styles.errorBanner, children: loadError }), loadingData ? (_jsx("div", { style: styles.loadingCenter, children: _jsx("div", { style: styles.spinner }) })) : (_jsxs("form", { onSubmit: handleSubmit, style: styles.card, children: [_jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Template" }), _jsxs("select", { style: styles.select, value: selectedTemplateId, onChange: (e) => handleTemplateChange(e.target.value), disabled: submitting, children: [_jsx("option", { value: "", children: "None (custom task)" }), templates.map((t) => (_jsx("option", { value: t.id, children: t.name }, t.id)))] })] }), _jsxs("div", { style: styles.field, children: [_jsxs("label", { style: styles.label, children: ["Task Name ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { type: "text", style: styles.input, value: name, onChange: (e) => setName(e.target.value), placeholder: "Enter task name", disabled: submitting, required: true })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Instructions" }), _jsx("textarea", { style: styles.textarea, value: instructions, onChange: (e) => setInstructions(e.target.value), placeholder: "Optional instructions or details", disabled: submitting, rows: 4 })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Due Date" }), _jsx("input", { type: "date", style: styles.input, value: dueDate, onChange: (e) => setDueDate(e.target.value), disabled: submitting })] }), _jsxs("div", { style: styles.field, children: [_jsx("label", { style: styles.label, children: "Assign To" }), _jsxs("select", { style: styles.select, value: assignedInstallerId, onChange: (e) => setAssignedInstallerId(e.target.value), disabled: submitting, children: [_jsx("option", { value: "", children: "Unassigned" }), installers.map((inst) => (_jsxs("option", { value: inst.id, children: [inst.firstName, " ", inst.lastName] }, inst.id)))] })] }), submitError && _jsx("div", { style: styles.errorBanner, children: submitError }), _jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", style: styles.cancelButton, onClick: () => navigate('/tasks'), disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", style: { ...styles.submitButton, opacity: submitting ? 0.6 : 1 }, disabled: submitting, children: submitting ? 'Creating…' : 'Create Task' })] })] }))] }) }));
};
const styles = {
    pageHeader: {
        marginBottom: '24px',
    },
    backButton: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 8px 0',
        display: 'block',
        minHeight: '44px',
    },
    heading: {
        fontSize: '34px',
        fontWeight: '700',
        margin: '0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    card: {
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        padding: '28px',
    },
    field: {
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#3c3c43',
        marginBottom: '6px',
        letterSpacing: '-0.1px',
    },
    required: {
        color: '#c0392b',
    },
    input: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
    },
    textarea: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        boxSizing: 'border-box',
        minHeight: '100px',
    },
    select: {
        width: '100%',
        padding: '10px 14px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        fontSize: '15px',
        color: '#1d1d1f',
        outline: 'none',
        boxSizing: 'border-box',
        minHeight: '44px',
        cursor: 'pointer',
    },
    errorBanner: {
        padding: '12px 16px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '10px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '8px',
    },
    cancelButton: {
        padding: '12px 24px',
        backgroundColor: 'rgba(0,0,0,0.05)',
        color: '#3c3c43',
        border: '1px solid rgba(0,0,0,0.1)',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    submitButton: {
        padding: '12px 24px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    loadingCenter: {
        display: 'flex',
        justifyContent: 'center',
        padding: '60px 0',
    },
    spinner: {
        width: '32px',
        height: '32px',
        border: '3px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
};
//# sourceMappingURL=TaskCreate.js.map